<script lang="ts" setup>

import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { GeoJSONSource } from "maplibre-gl";
import { addLayer, removeLayersAndSources, injectMap } from "@/utils/MapUtils";
import { type ProgressMode } from "@/shared/types/types";
import { safeDarken, safeLighten } from "@/utils/Color";
import { storeToRefs } from "pinia";
import { useStore } from "@/store/Store";
import { useProgressStore } from "@/store/ProgressStore";
import { TFeatureCollection } from '@/shared/types/geojson';
import { getBackgroundRoutes } from "@/shared/utils/routes";

const $map = injectMap();
const store = useStore();
const progressStore = useProgressStore();

const props = defineProps<{
	progress?: number;
	progressMode?: ProgressMode;
	editMode?: boolean;
}>();

const emit = defineEmits<{
	setProgress: [progress: number, source: "track" | "map"];
}>();

const { trackFeature, trackFeatureRenderLegs, selectedRouteId, routes, isDarkMap, map } = storeToRefs(store);

const previousProgress = ref(1);
const createdLayers = ref(0);

const trackColor = computed((): string => {
	return trackFeature.value.properties?.color || "#000";
});

const inactiveColor = computed((): string => {
	if (customInactiveColor.value) {
		return trackFeature.value.properties?.inactiveColor || "#000";
	} else {
		return safeDarken(trackColor.value, 0.27);
	}
});

const customInactiveColor = computed((): boolean => {
	return trackFeature.value.properties?.customInactiveColor;
});

const thickness = computed((): number => {
	return Number(trackFeature.value.properties?.thickness) || 8;
});

const baseThickness = computed((): number => {
	//return Math.min(this.thickness, 3); //previously
	return thickness.value;
});

const baseSource = computed((): GeoJSONSource => {
	return $map.getSource('line-base') as GeoJSONSource
});

const borderSource = computed((): GeoJSONSource => {
	return $map.getSource('line-border') as GeoJSONSource
});

const unselectedSource = computed((): GeoJSONSource => {
	return $map.getSource('line-unselected') as GeoJSONSource
});

const unselectedRoutes = computed<TFeatureCollection>(() => {
	return {
		type: "FeatureCollection",
		features: getBackgroundRoutes(routes.value, selectedRouteId.value, map.value?.showInactiveRoutes)
			.map(route => {
				return {
					type: "Feature",
					id: route.id,
					geometry: {
						type: "LineString",
						coordinates: route.geometry.coordinates.flat(),
					},
					properties: { ...route.properties },
				}
			})
	}
});

function getSource(name: string): GeoJSONSource {
	return $map.getSource(name) as GeoJSONSource;
}

function createAnimationLayers() {
	const legs = trackFeatureRenderLegs.value ?? [];

	//to many created layers? remove some.
	if (createdLayers.value > legs.length) {
		for (let i = legs.length; i < createdLayers.value; i++) {
			if ($map.getLayer("line-animation-" + i)) $map.removeLayer("line-animation-" + i);
			if ($map.getSource("line-animation-" + i)) $map.removeSource("line-animation-" + i);
		}
		createdLayers.value = legs.length;
	}

	// already created layers? update data
	for (let i = 0; i < createdLayers.value; i++) {
		(getSource('line-animation-' + i))?.setData(legs[i]);
	}

	// too few created layers? add some.
	for (let i = createdLayers.value; i < legs.length; ++i) {
		//TODO: layer z index
		addLayer($map, {
			id: 'line-animation-' + i,
			type: 'line',
			source: {
				type: 'geojson',
				lineMetrics: true,
				data: legs[i],
				tolerance: .5,
			},
			layout: {
				'line-cap': 'round',
				'line-join': 'round'
			},
			paint: {
				'line-color': legs[i].properties!.color,
				'line-width': thickness.value,
				'line-opacity': 1,
				'line-offset': ['number', ['get', 'lineOffset'], 0],
				'line-gradient': [
					'interpolate',
					['linear'],
					['line-progress'],
					0, "transparent", 1, "transparent"
				]
			}
		}, false, "line-placeholder");
	}
	createdLayers.value = legs.length;
}


function drawMap() {
	if (getSource("line-animation-0") && !$map.isSourceLoaded("line-animation-0")) {
		return requestAnimationFrame(drawMap)
	}

	const colorTrans = "transparent";
	const little = 0.000000001;
	const fadeLength = 0.1; //maybe base on actual distance??
	const currentProgress = props.progress ?? 0;
	const progressFadeStart = currentProgress - fadeLength;

	for (let i = 0; i < createdLayers.value; i++) {

		//TODO: only update if needed, based on previous progress
		const changeStart = Math.min(previousProgress.value, currentProgress) - 0.1;
		const changeEnd = Math.max(previousProgress.value, currentProgress);


		const values = [] as (number | string)[];
		const leg = trackFeatureRenderLegs.value![i];
		if (!leg) return
		const legStart = leg.properties!.startProgress ?? 0;
		const legEnd = leg.properties!.endProgress ?? 1;
		const legLength = legEnd - legStart;

		const fadeStartLocal = (progressFadeStart - legStart) / legLength;
		const fadeEndLocal = (currentProgress - legStart) / legLength;
		const color = leg.properties!.color;
		if (fadeEndLocal <= 0) {
			// we are before the leg
			values.push(0, colorTrans)
		} else if (fadeStartLocal >= 1 || currentProgress >= 1) {
			// we have passed the leg
			values.push(0, color)
		} else {
			// // we are in the leg
			values.push(fadeStartLocal, color)
			values.push(fadeEndLocal, safeLighten(color, 0.25));
			values.push(fadeEndLocal + little, colorTrans);
		}

		if ($map.getLayer("line-animation-" + i)) {
			$map.setPaintProperty("line-animation-" + i, "line-gradient", [
				'interpolate',
				['linear'],
				['line-progress'],
				//@ts-ignore
				...values
			])
		}
	}

	previousProgress.value = currentProgress;
}


function initMouse() {

	$map.on('mousemove', e => {
		if (props.editMode)
			return;

		progressStore.handleMouseMoveMap(e.lngLat.toArray());
	});
}

function initMap() {
	addLayer($map, {
		id: 'line-unselected',
		type: 'line',
		source: {
			type: 'geojson',
			lineMetrics: true,
			data: unselectedRoutes.value,
			tolerance: .8,
		},
		layout: {
			'line-cap': 'round',
			'line-join': 'round'
		},
		paint: {
			'line-color': ['get', 'color'],
			'line-width': 2,
			'line-opacity': .45,
			'line-offset': ['number', ['get', 'lineOffset'], 0],
		}
	});

	addLayer($map, {
		id: 'line-border',
		type: 'line',
		source: {
			type: 'geojson',
			lineMetrics: false,
			data: trackFeature.value,
			tolerance: .5,
		},
		layout: {
			'line-cap': 'round',
			'line-join': 'round'
		},
		paint: {
			'line-color': "#fff",
			'line-width': baseThickness.value + (isDarkMap.value ? 2 : 4),
			'line-opacity': isDarkMap.value ? 0.85 : 1,
			'line-offset': ['number', ['get', 'lineOffset'], 0],
		}
	});
	addLayer($map, {
		id: 'line-base',
		type: 'line',
		source: {
			type: 'geojson',
			lineMetrics: false,
			data: trackFeature.value,
			tolerance: .5,
		},
		layout: {
			'line-cap': 'round',
			'line-join': 'round'
		},
		paint: {
			'line-color': inactiveColor.value,
			'line-width': baseThickness.value - 0.5,
			'line-opacity': 1,
			'line-offset': ['number', ['get', 'lineOffset'], 0],

		}
	});

	addLayer($map, {
		id: 'line-placeholder',
		type: 'line',
		source: {
			type: 'geojson',
			data: {
				type: "FeatureCollection",
				features: [],
			},
		},
	}, false);

	createAnimationLayers();

	initMouse();

	drawMap();
}

watch(() => props.progress, () => {
	drawMap();
});

watch(trackFeature, (newTrackFeature) => {
	if (newTrackFeature) {
		baseSource.value?.setData(newTrackFeature);
		borderSource.value?.setData(newTrackFeature);
		// drawMap() is called in trackFeatureRenderLegs watcher
	}
}, { deep: true }); // Deep watch might be needed if properties change internally

watch(trackFeatureRenderLegs, () => {
	createAnimationLayers();
	drawMap();
}, { deep: true });

watch(unselectedRoutes, (newRoutes) => {
	unselectedSource.value?.setData(newRoutes);
});

watch(trackColor, () => {
	drawMap();
	if ($map.getLayer('line-base')) {
		$map.setPaintProperty("line-base", "line-color", inactiveColor.value);
	}
});

watch(inactiveColor, (newColor) => {
	if ($map.getLayer('line-base')) {
		$map.setPaintProperty("line-base", "line-color", newColor);
	}
});

// Watch customInactiveColor to update inactiveColor which in turn updates the paint property
// watch(customInactiveColor, () => {
// 	// This watcher might be redundant if inactiveColor watcher handles it
// 	if ($map.getLayer('line-base')) {
// 		$map.setPaintProperty("line-base", "line-color", inactiveColor.value);
// 	}
// });

watch(thickness, (newThickness) => {
	for (let i = 0; i < createdLayers.value; i++) {
		if ($map.getLayer("line-animation-" + i)) {
			$map.setPaintProperty("line-animation-" + i, "line-width", newThickness);
		}
	}
	if ($map.getLayer('line-base')) {
		$map.setPaintProperty("line-base", "line-width", baseThickness.value - .5);
	}
	if ($map.getLayer('line-border')) {
		$map.setPaintProperty("line-border", "line-width", baseThickness.value + 4);
	}
});

onMounted(() => {
	// Ensure map is loaded before initializing
	if ($map.isStyleLoaded()) {
		initMap();
	} else {
		$map.once('load', initMap);
	}
});

onBeforeUnmount(() => {
	removeLayersAndSources($map, ["line-base", "line-border", "line-unselected", "line-placeholder"]);
	const layersToRemove = Array.from({ length: createdLayers.value }, (_, i) => "line-animation-" + i);
	removeLayersAndSources($map, layersToRemove);
	// Remove mousemove listener if added directly
	$map.off('mousemove', initMouse); // Assuming initMouse adds the listener, need to ensure correct removal
});

</script>

<template></template>